import { memo } from 'react';
import { Spin } from 'antd';
import styled from 'styled-components';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';

import { DiligenceReport } from 'types';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ChartDataLabels);

// Function to format large numbers with B, M, T suffixes
const formatNumber = (value: number): string => {
  if (value >= 1e12) {
    return (value / 1e12).toFixed(1) + 'T';
  } else if (value >= 1e9) {
    return (value / 1e9).toFixed(1) + 'B';
  } else if (value >= 1e6) {
    return (value / 1e6).toFixed(1) + 'M';
  } else if (value >= 1e3) {
    return (value / 1e3).toFixed(1) + 'K';
  }
  return value.toString();
};

const createChartOptions = (fullNamesMap: { [key: string]: string } = {}) => ({
  responsive: true,
  plugins: {
    legend: {
      display: true,
      position: 'bottom' as const,
      labels: {
        usePointStyle: true,
        pointStyle: 'rect',
        padding: 20,
        font: {
          size: 12,
          weight: '500',
        },
        color: '#374151',
      },
    },
    datalabels: {
      display: true,
      anchor: 'end' as const,
      align: function (context: any) {
        const chart = context.chart;
        const datasetIndex = context.datasetIndex;
        const dataIndex = context.dataIndex;
        const value = context.dataset.data[dataIndex];
        const chartArea = chart.chartArea;

        // Calculate the bar's top position
        const meta = chart.getDatasetMeta(datasetIndex);
        const element = meta.data[dataIndex];
        const barTop = element.y;

        // If the bar is too close to the top (within 30px), position label inside the bar
        if (barTop < chartArea.top + 30) {
          return 'start'; // Position label at the bottom of the bar (inside)
        }
        return 'top'; // Position label above the bar
      },
      formatter: function (value: number) {
        return formatNumber(value);
      },
      font: {
        size: 10,
        weight: 'bold',
      },
      color: function (context: any) {
        const chart = context.chart;
        const datasetIndex = context.datasetIndex;
        const dataIndex = context.dataIndex;
        const chartArea = chart.chartArea;

        // Calculate the bar's top position
        const meta = chart.getDatasetMeta(datasetIndex);
        const element = meta.data[dataIndex];
        const barTop = element.y;

        // If label is inside the bar, use white color for better contrast
        if (barTop < chartArea.top + 30) {
          return '#ffffff';
        }
        return '#374151'; // Default dark color for labels above bars
      },
      padding: 4,
      clip: false, // Allow labels to be drawn outside the chart area if needed
    },
    tooltip: {
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      titleColor: '#fff',
      bodyColor: '#fff',
      borderColor: '#e5e7eb',
      borderWidth: 1,
      cornerRadius: 8,
      displayColors: true,
      padding: 12,
      titleFont: {
        size: 14,
        weight: 'bold',
      },
      bodyFont: {
        size: 13,
      },
      callbacks: {
        title: function (context: any) {
          const shortName = context[0].label;
          const fullName = fullNamesMap[shortName] || shortName;
          return fullName;
        },
        label: function (context: any) {
          const label = context.dataset.label || '';
          const value = context.parsed.y;
          return `${label}: ${value.toLocaleString()}`; // Show original unformatted value
        },
      },
    },
  },
  maintainAspectRatio: false,
  layout: {
    padding: {
      top: 40, // Add padding at the top for labels
      bottom: 10,
      left: 10,
      right: 10,
    },
  },
  scales: {
    x: {
      grid: {
        display: false,
      },
      ticks: {
        font: {
          size: 12,
          weight: '600',
        },
        color: '#374151',
      },
    },
    y: {
      beginAtZero: true,
      suggestedMax: function (context: any) {
        // Add 10% padding to the maximum value to ensure labels fit
        const max = Math.max(
          ...context.chart.data.datasets.flatMap((dataset: any) => dataset.data),
        );
        return max * 1.1;
      },
      grid: {
        display: true,
        color: 'rgba(0, 0, 0, 0.05)',
        borderDash: [2, 2],
      },
      ticks: {
        font: {
          size: 11,
          weight: '500',
        },
        color: '#6b7280',
        callback: function (value: any) {
          return formatNumber(Number(value));
        },
      },
    },
  },
  elements: {
    bar: {
      borderRadius: 0, // Rectangular bars as requested
      borderSkipped: false,
    },
  },
  interaction: {
    intersect: false,
    mode: 'index' as const,
  },
});

interface DiligenceChartProps {
  loading: boolean;
  dataChart: DiligenceReport;
  fullNamesMap?: { [key: string]: string };
  onColumnClick?: (label: string, type: string) => void;
}

const StyledWrapperChart = styled.div`
  width: 100%;
  height: 350px;
`;

function PayrollSummaryLocationDepartmentChart({
  loading,
  dataChart,
  fullNamesMap = {},
  onColumnClick,
}: DiligenceChartProps) {
  const data: DiligenceReport = {
    labels: dataChart.labels,
    datasets:
      dataChart &&
      dataChart.datasets &&
      dataChart.datasets.map((x, index) => ({
        ...x,
        barThickness: 40, // Wider bars for better visibility
        maxBarThickness: 50,
        borderRadius: 0, // Rectangular bars
        borderSkipped: false,
        // Enhanced colors for grouped bars
        backgroundColor:
          [
            '#FF6B6B', // Overtime - Red
            '#4ECDC4', // Insurance - Teal
            '#45B7D1', // PIT - Blue
            '#96CEB4', // Gross Salary - Green
          ][index] || x.backgroundColor,
        borderColor: ['#FF5252', '#26A69A', '#2196F3', '#66BB6A'][index] || x.backgroundColor,
        borderWidth: 1,
      })),
  };

  const chartOptions = createChartOptions(fullNamesMap);

  return (
    <Spin spinning={loading}>
      <StyledWrapperChart>
        <Bar
          options={chartOptions}
          data={data}
          getElementAtEvent={(elements, event) => {
            if (event.type === 'click' && elements.length) {
              const shortLabel = dataChart.labels[elements[0].index];
              const fullLabel = fullNamesMap[shortLabel] || shortLabel;
              const type = dataChart.datasets[elements[0].datasetIndex].label;

              onColumnClick?.(fullLabel, type);
            }
          }}
        />
      </StyledWrapperChart>
    </Spin>
  );
}

export default memo(PayrollSummaryLocationDepartmentChart);
